import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../context/useAuth';
import { useI18n } from '../context/useI18n';
import { resolveConsumerSupportWaUrl } from '../utils/whatsappSupport';
import HelpAssistantWidget from './HelpAssistantWidget';
import { getServicesFor } from './dashboard/userServices';
import { useFrontendConfig } from '../context/FrontendConfigContext';
import { isSedeFinalUser } from '../utils/gymRoleScope';

function pickFaqModulo(services) {
  if (services.some((s) => s.id === 'futbol')
    && !services.some((s) => s.id === 'd28d' || s.id === 'live-classes' || s.id === 'training' || s.id === 'food-plan')) {
    return 'futbol';
  }
  if (services.some((s) => s.id === 'd28d' || s.id === 'live-classes')) return 'd28d';
  if (services.some((s) => s.id === 'training')) return 'training';
  return 'platform';
}

export default function SupportCenter() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const { config: frontendConfig } = useFrontendConfig();
  const [faq, setFaq] = useState({ categories: [], items: [] });
  const [q, setQ] = useState('');
  const [planSupport, setPlanSupport] = useState(null);
  const [gymInfo, setGymInfo] = useState(null);
  const [trainerInfo, setTrainerInfo] = useState(null);

  const services = useMemo(
    () => getServicesFor(user, frontendConfig, lang),
    [user, frontendConfig, lang],
  );
  const modulo = pickFaqModulo(services);
  const foodOnly = services.some((s) => s.id === 'food-plan')
    && !services.some((s) => s.id === 'd28d' || s.id === 'live-classes' || s.id === 'training');
  const isSedeMember = isSedeFinalUser(user, gymInfo);

  useEffect(() => {
    api.get(`/faq/${modulo}`).then((r) => setFaq(r.data?.data || { categories: [], items: [] })).catch(() => {});
    api.get('/accounts/me')
      .then((r) => setPlanSupport(r.data?.plan_support || null))
      .catch(() => {
        api.get('/communications/support')
          .then((r) => setPlanSupport(r.data?.data || null))
          .catch(() => {});
      });
    api.get('/accounts/my-services')
      .then((r) => {
        if (r.data?.gym) setGymInfo(r.data.gym);
        if (r.data?.coach) setTrainerInfo(r.data.coach);
      })
      .catch(() => {});
    const gymId = user?.gym_id ?? user?.gymId;
    const trainerId = user?.trainer_id ?? user?.trainerId;
    if (gymId) {
      api.get(`/gyms/${gymId}`)
        .then((r) => setGymInfo((prev) => prev || r.data?.data || r.data || null))
        .catch(() => {});
    }
    if (trainerId) {
      api.get(`/trainers/${trainerId}`)
        .then((r) => setTrainerInfo((prev) => prev || r.data?.data || r.data || null))
        .catch(() => {});
    }
  }, [modulo, user?.gym_id, user?.gymId, user?.trainer_id, user?.trainerId]);

  const filtered = (faq.items || []).filter((item) => {
    const text = `${item.pregunta || ''} ${item.respuesta || ''}`.toLowerCase();
    return !q.trim() || text.includes(q.trim().toLowerCase());
  });

  const waUrl = resolveConsumerSupportWaUrl({
    gym: gymInfo,
    trainer: trainerInfo,
    planSupport,
    sedeFirst: isSedeMember || gymInfo?.parent_gym_id != null,
    user,
  });

  return (
    <div className="dashboard-main-view surface-readable space-y-6 max-w-3xl">
      <header>
        <h1 className="d28d-page-title text-2xl font-bold">{t('nav.support', 'Soporte')}</h1>
        <p className="text-sm d28d-text-muted mt-1">
          {t('support.subtitle', 'Preguntas frecuentes y contacto con tu equipo.')}
        </p>
      </header>

      <div className="card p-4 flex flex-wrap gap-3 items-center">
        <a href={waUrl} target="_blank" rel="noreferrer" className="btn-primary">
          {t('support.whatsapp', 'Contactar por WhatsApp')}
        </a>
      </div>

      <div>
        <input
          className="input w-full"
          placeholder={t('support.search_faq', 'Buscar en preguntas frecuentes…')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="space-y-3">
        {filtered.length === 0 ? (
          <p className="d28d-text-muted text-sm">{t('support.no_faq', 'Sin resultados.')}</p>
        ) : (
          filtered.map((item) => (
            <details key={item.id} className="card p-4">
              <summary className="font-semibold cursor-pointer">{item.pregunta}</summary>
              <p className="text-sm d28d-text-muted mt-2 whitespace-pre-wrap">{item.respuesta}</p>
            </details>
          ))
        )}
      </div>

      {!foodOnly ? <HelpAssistantWidget modulo={modulo} supportWaUrl={waUrl} planSupport={planSupport} /> : null}
    </div>
  );
}
